import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import ImageCarousel from '@/components/ImageCarousel';

/**
 * ── /components/image-carousel ───────────────────────────────────────────
 *
 * ImageCarousel — the event page's hero (`app/events/[id]/index.tsx`, the
 * `<ImageCarousel>` right under the header, negative-margined to bleed edge-
 * to-edge behind the transparent header). The real call there is:
 *
 *   <ImageCarousel
 *     images={event.images.map((i) => i.url)}
 *     badgeLabel={heroBadgeLabel ?? undefined}
 *     badgeVariant="outline"
 *     badgeSize="large"
 *     grayScale={event.isSoldOut || event.isInPast}
 *   />
 *
 * `images` is a bare `string[]` of URLs — the screen flattens the API's
 * `EventImage[]` (`{id, url, isPrimary, ...}`, `src/api/eventsApi.ts`) to
 * `.url` itself before handing it over; ImageCarousel never sees the object
 * shape or `isPrimary` (ordering/primary-pick is the caller's job).
 *
 * `badgeLabel` + `grayScale` are driven together by two flags
 * on `event`, computed once in `EventBody` (events/[id]/index.tsx ~184-191):
 *   - `event.isInPast` → badge "Missed out" (always `outline`/`large`),
 *     `grayScale={true}` — every slide goes monotone via `MaybeGrayscale`.
 *   - `event.isSoldOut` → the same pairing, labelled "Sold out".
 *   - sold-out wins if an event is somehow both (comment on the real
 *     screen: "if an event is both sold out and in past then sold out
 *     badge wins").
 *   - neither flag → no `badgeLabel` at all, no grayscale: the plain case.
 *
 * Pagination dots + wraparound goTo() only render/matter once `images.length
 * > 1`; a single-image event (or a not-yet-approved event's own preview,
 * `event.category === 'preview'`) collapses to just the photo, no dots. Web
 * gets clickable prev/next arrows too (device relies on swipe) — open this
 * route in a browser to see them; Platform.OS !== 'web' on device hides them
 * entirely, nothing to toggle here.
 *
 * Not previewed: the one-frame `width === 0` placeholder before `onLayout`
 * fires (a bare 1px `View`, gone by the next paint) — real but not a state
 * a static page can usefully pin.
 *
 * Each box below is fixed-width because ImageCarousel measures its own
 * container via `onLayout` and derives height from that width (3:4 ratio,
 * see the component's docstring) — it has no width prop of its own to pass.
 * ─────────────────────────────────────────────────────────────────────────
 */

const CAROUSEL_WIDTH = 240;

// Same picsum fixtures the old all.tsx holding-pen used, split so a single-
// vs multi-image event can be compared directly.
const SINGLE_IMAGE = ['https://picsum.photos/id/1015/600/800'];
const MULTIPLE_IMAGES = [
  'https://picsum.photos/id/1015/600/800',
  'https://picsum.photos/id/1025/600/800',
  'https://picsum.photos/id/1035/600/800',
];

export default function ImageCarouselPreview() {
  return (
    <>
      <Head>
        <title>ImageCarousel · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* images.length === 1 — no pagination dots and, on web, no arrows
            either (`count > 1` gates both). Just the photo. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              single image — no dots, no arrows (count gates both)
            </Text>
            <YStack width={CAROUSEL_WIDTH}>
              <ImageCarousel images={SINGLE_IMAGE} />
            </YStack>
          </YStack>

          {/* Multiple images — live: swipe on device, or on web tap a dot /
            the arrows. The active dot is a 20×8 pill, the rest 8×8 circles;
            index is tracked on every scroll frame (not just momentum end) so
            the dots track a drag in real time on both platforms. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — multiple images: swipe (device) or tap a dot / the web
              arrows to page; wraps around at either end instead of disabling
            </Text>
            <YStack width={CAROUSEL_WIDTH}>
              <ImageCarousel images={MULTIPLE_IMAGES} />
            </YStack>
          </YStack>

          {/* isInPast — the same badge + grayscale pairing as isSoldOut
            below; only the label differs. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              event.isInPast — badgeLabel=&quot;Missed out&quot;, outline/large,
              grayScale grayscales every slide
            </Text>
            <YStack width={CAROUSEL_WIDTH}>
              <ImageCarousel
                images={MULTIPLE_IMAGES}
                badgeLabel="Missed out"
                badgeVariant="outline"
                badgeSize="large"
                grayScale
              />
            </YStack>
          </YStack>

          {/* isSoldOut — badgeLabel + grayScale together, exactly as the
            real screen pairs them. Tiers still render below this on the
            real page — isSoldOut doesn't hide the picker, just dims the hero
            and every slide in it via MaybeGrayscale. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              event.isSoldOut — badgeLabel=&quot;Sold out&quot;, outline/large,
              grayScale grayscales every slide (wins over isInPast if both are
              true)
            </Text>
            <YStack width={CAROUSEL_WIDTH}>
              <ImageCarousel
                images={MULTIPLE_IMAGES}
                badgeLabel="Sold out"
                badgeVariant="outline"
                badgeSize="large"
                grayScale
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
